import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {
  EntitySelector,
  EntitySelectorAutoTabs,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorHandle,
  type EntitySelectorConfig,
  type EntityItemConfig,
} from "@salestrak/ui";
import { useUserPool, useTeamPool } from "#app/components/entity-selectors/hooks";
import type { EntityKind, MentionInsert } from "./entity-mention";
import { MENTIONABLE_ENTITY_KINDS } from "./mention-sources";
import { SuggestionPanel, type SuggestionRenderHandle } from "./suggestion-list";
import type { Omittable } from "@salestrak/ui";

type MentionMenuProps = Omittable<{
  kind: EntityKind;
  query: string;
  command: (item: MentionInsert) => void;
  maxHeight?: number;
  ref?: React.Ref<SuggestionRenderHandle>;
}>;

export function MentionMenu({
  kind,
  query,
  command,
  maxHeight,
  ref,
}: MentionMenuProps) {
  // Pools are keyed by entity kind; which ones the menu shows is driven by
  // MENTIONABLE_ENTITY_KINDS (members only today). Both hooks are called
  // unconditionally — Rules of Hooks — then filtered into `pools`, so a kind
  // is enabled/disabled purely by editing that list. With a single pool,
  // EntitySelectorAutoTabs renders no tabs.
  const { pool: userPool } = useUserPool();
  const { pool: teamPool } = useTeamPool();
  const selectorRef = useRef<EntitySelectorHandle>(null);

  const pools = useMemo(() => {
    const byKind: Partial<Record<EntityKind, typeof userPool>> = {
      user: userPool,
      team: teamPool,
    };
    return MENTIONABLE_ENTITY_KINDS.map((kind) => byKind[kind]).filter(
      (pool): pool is typeof userPool => pool != null,
    );
  }, [userPool, teamPool]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        // Tab cycles pools even when the active one is empty, so you can reach
        // a populated tab without the mouse.
        if (event.key === "Tab") {
          selectorRef.current?.cycleActivePool(event.shiftKey ? -1 : 1);
          return true;
        }
        // Active pool empty — let the key through so Enter/arrows act on the editor.
        if (!selectorRef.current?.getActiveId()) return false;
        if (event.key === "ArrowUp") {
          selectorRef.current.move(-1);
          return true;
        }
        if (event.key === "ArrowDown") {
          selectorRef.current.move(1);
          return true;
        }
        if (event.key === "Enter") {
          selectorRef.current.selectActive();
          return true;
        }
        return false;
      },
    }),
    [],
  );

  // Insert the mention node, then return false to veto the selector's own
  // selection state — the editor document owns the inserted chip.
  const handleBeforeSelect = useCallback(
    (entity: EntityItemConfig) => {
      command({
        id: entity.id,
        label: entity.title,
        entity: (entity.data?.entityType as EntityKind | undefined) ?? kind,
        workflowId: (entity.data?.workflowId as string | undefined) ?? null,
      });
      return false;
    },
    [command, kind],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools,
      maxSelections: 1,
      emptyMessage: "No members found",
    }),
    [pools],
  );

  return (
    <SuggestionPanel minWidth={280} maxWidth={340} maxHeight={maxHeight}>
      <EntitySelector
        ref={selectorRef}
        variant="inline"
        config={config}
        search={query}
        onBeforeSelect={handleBeforeSelect}
      >
        <EntitySelectorAutoTabs />
        {pools.map((pool) => (
          <EntitySelectorPool key={pool.id} value={pool.id} />
        ))}
        <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
      </EntitySelector>
    </SuggestionPanel>
  );
}
